import _ from "lodash";
import angular from "angular";
import html from "./svg.tmpl.html";

export default {
    templateUrl: html,
    controllerAs: "svg",
    bindings: {
        svgCode: "<",
        components: "<",
        selection: "<?",
        onComponentClick: "<?",
        onVisibleColorsChanged: "&?",
        onMouseOver: "<?",
        onMouseOut: "<?",
        colorProperty: "@",
        parentId: "@",
    },
    controller: function SvgComponentCtrl(
        $document,
        $timeout,
        $rootScope,
        $sbSvgGenerate,
        $sbSketchVisualizationSetting,
        $log,
        MODES,
        EVENTS,
        visualizationService
    ) {
        "ngInject";

        /////////////////////
        //
        //      Direct variables
        //
        /////////////////////

        const HOVER_BORDER_MAX_PX = 8;
        const HOVER_BORDER_MIN_PX = 2;
        const HOVER_BORDER_SCALE = 4;
        const DEFAULT_SVG_STROKE_WIDTH = 1;
        const CSS_UNIT_VALUE_REGEX = /^([+-]?(?:\d+|\d*\.\d+))([a-z]*|%)$/;
        const STATES_THAT_CAN_BE_COMBINED_WITH_OUT_OF_SEQUENCE = [
            "DONE",
            "CONFIRMED",
        ];

        const CONTAINER_ID = "sb-svg";
        const CLICKABLE_ATTRIBUTE = "data-sb-clickable";
        const CLICKABLE_SELECTOR = `[${CLICKABLE_ATTRIBUTE}="true"]`;
        const ORIGINAL_STROKE_WIDTH_ATTRIBUTE = "data-sb-original-stroke-width";

        // keep current size as reference
        let currentPatternSize = $sbSvgGenerate.computePatternSize([]);

        let currentSvgCode = "";
        let currentComponents = [];
        let currentSelection = [];

        const originalStrokes = new Map();
        const originalStrokeStyles = new Map();

        var currentStructureStyles = new Map();

        var vm = this;

        /////////////////////
        //
        //      WATCHER
        //
        /////////////////////

        var updateListener = $rootScope.$on(
            EVENTS.SVG__UPDATE_ELEMENT,
            function (event, data) {
                _findAndSetSvgElement(data);
            }
        );

        const borderListener = $rootScope.$watch(function () {
            return $sbSketchVisualizationSetting.model.showBorder;
        }, _updateSvgBorderColors);

        const outOfSequenceListener = $rootScope.$watch(function () {
            return $sbSketchVisualizationSetting.model.markOutOfSequence;
        }, _reMatchComponents);

        /////////////////////
        //
        //      Lifecycle Hooks
        //          docu: https://toddmotto.com/angular-1-5-lifecycle-hooks
        //
        /////////////////////

        vm.$onDestroy = function () {
            updateListener();
            borderListener();
            outOfSequenceListener();
        };

        vm.$onChanges = function (changes) {
            if (
                changes.svgCode &&
                changes.svgCode.currentValue !== changes.svgCode.previousValue
            ) {
                if (changes.svgCode.currentValue.length > 0) {
                    currentSvgCode = _removeSvgExistingClasses(
                        changes.svgCode.currentValue
                    );
                }
            }
            if (
                changes.components &&
                changes.components.currentValue !==
                    changes.components.previousValue
            ) {
                if (changes.components.currentValue.length > 0) {
                    currentComponents = changes.components.currentValue;
                }
            }

            if (_hasSelectionChanged(changes)) {
                currentSelection = changes.selection.currentValue;
            }

            if (currentSvgCode || currentComponents || currentSelection) {
                // wrap the svg calculation in a timeout to allow the digest cycle to
                // change the ready state. Otherwise the $onChange digest collection
                // is updating the DOM only once
                return $timeout(function () {
                    $sbSvgGenerate.resetSvgPatterns();
                    _attachSvg(currentSvgCode, CONTAINER_ID);
                    _updateCurrentPatternSize();
                    _matchComponents(currentComponents, currentSvgCode);
                    _colorStructureElements();
                    _reportVisibleColors();
                }, 0);
            }
        };

        /////////////////////
        //
        //      IMPL
        //
        /////////////////////

        function _hasSelectionChanged(changes) {
            return (
                changes.selection &&
                _.get(changes, "selection.currentValue") !==
                    _.get(changes, "selection.previousValue")
            );
        }

        function _removeSvgExistingClasses(svgCode) {
            // eslint-disable-next-line no-useless-escape
            const removeClassTagRegExpWithDoubleQuotes = /class=\"[^\"]+\"/gim;
            // eslint-disable-next-line no-useless-escape
            const removeClassTagRegExpWithSingleQuotes = /class=\'[^\']+\'/gim;
            return svgCode
                .replace(removeClassTagRegExpWithDoubleQuotes, "")
                .replace(removeClassTagRegExpWithSingleQuotes, "");
        }

        function _attachSvg(svgCode, containerId) {
            const containerElement = $document[0].getElementById(containerId);
            if (!containerElement) {
                $log.error(
                    `Container with id ${containerId} not found. SVG cannot be attached.`
                );
                return;
            }

            var svgContainer = angular.element(containerElement);

            svgContainer.empty();
            svgContainer.off("click");
            svgContainer.on("click", _clickElement);
            svgContainer.off("mouseover");
            svgContainer.off("mouseout");

            if (
                !_is_touch_device4() &&
                _.isFunction(vm.onMouseOver) &&
                _.isFunction(vm.onMouseOut)
            ) {
                svgContainer.on("mouseover", _.debounce(vm.onMouseOver, 400));
                svgContainer.on("mouseover", placeHoverColor);
                svgContainer.on("mouseout", vm.onMouseOut);
                svgContainer.on("mouseout", revertHoverColor);
            }

            svgContainer.append(svgCode);
            _setViewBox(svgContainer);
            _decorateInteractiveElements(svgContainer[0], currentComponents);
        }

        function _decorateInteractiveElements(svgContainer, components) {
            if (!svgContainer) {
                return;
            }

            _markStructureElementsAsInteractive(svgContainer);
            _markDeliverableElementsAsInteractive(svgContainer, components);
        }

        function _markStructureElementsAsInteractive(svgContainer) {
            const structureElements = svgContainer.querySelectorAll(
                '[data-sb-type="structure"]'
            );

            _.forEach(structureElements, (svgElement) => {
                _markElementAsInteractive(svgElement, "structure");
            });
        }

        function _markDeliverableElementsAsInteractive(
            svgContainer,
            components
        ) {
            _.forEach(components, (component) => {
                const svgElement = angular.element(
                    $document[0].getElementById(component.CODE)
                )[0];

                if (!svgElement || !svgContainer.contains(svgElement)) {
                    return;
                }

                svgElement.setAttribute("deliverable", component.ID);
                _markElementAsInteractive(svgElement, "deliverable");
            });
        }

        function _markElementAsInteractive(svgElement, type) {
            svgElement.setAttribute(CLICKABLE_ATTRIBUTE, "true");
            svgElement.setAttribute(
                ORIGINAL_STROKE_WIDTH_ATTRIBUTE,
                _getSourceStrokeWidth(svgElement)
            );
            svgElement.style.vectorEffect = "non-scaling-stroke";
            svgElement.style.strokeWidth = _getVisibleStrokeWidth(svgElement);
            svgElement.classList.add("sb-svg-element");
            svgElement.classList.add(`sb-svg-element--${type}`);
        }

        function _setViewBox(svgContainer) {
            const svgElement = svgContainer.children()[0];
            if (!svgElement) {
                return;
            }

            const viewBox = svgElement.getAttribute("viewBox");
            if (viewBox) {
                const viewBoxValues = viewBox.trim().split(/[\s,]+/);
                const width = Number.parseFloat(viewBoxValues[2]);
                const height = Number.parseFloat(viewBoxValues[3]);

                if (Number.isFinite(width) && Number.isFinite(height)) {
                    vm.viewBox = {
                        width,
                        height,
                    };
                    return;
                }
            }

            const widthAttribute = Number.parseFloat(
                svgElement.getAttribute("width") || ""
            );
            const heightAttribute = Number.parseFloat(
                svgElement.getAttribute("height") || ""
            );

            if (
                Number.isFinite(widthAttribute) &&
                Number.isFinite(heightAttribute)
            ) {
                vm.viewBox = {
                    width: widthAttribute,
                    height: heightAttribute,
                };
            }
        }

        //  IMPORTANT This is not 100% accurate
        // https://stackoverflow.com/questions/4817029/whats-the-best-way-to-detect-a-touch-screen-device-using-javascript
        function _is_touch_device4() {
            var prefixes = " -webkit- -moz- -o- -ms- ".split(" ");

            var mq = function (query) {
                return window.matchMedia(query).matches;
            };

            if (
                "ontouchstart" in window ||
                (window.DocumentTouch && document instanceof DocumentTouch) // eslint-disable-line
            ) {
                return true;
            }

            // include the 'heartz' as a way to have a non matching MQ to help terminate the join
            // https://git.io/vznFH
            var query = [
                "(",
                prefixes.join("touch-enabled),("),
                "heartz",
                ")",
            ].join("");
            return mq(query);
        }

        function _matchComponents(components) {
            originalStrokes.clear();
            originalStrokeStyles.clear();
            components.forEach(_findAndSetSvgElement);
            _storeStructureOriginalStrokes();
        }

        function _storeStructureOriginalStrokes() {
            const svgContainer = $document[0].getElementById(CONTAINER_ID);
            if (!svgContainer) {
                return;
            }
            const structureElements = svgContainer.querySelectorAll(
                '[data-sb-type="structure"]'
            );
            _.forEach(structureElements, (svgElement) => {
                if (svgElement.id) {
                    _storeOriginalStyles(svgElement, svgElement.id);
                }
            });
        }

        function _findAndSetSvgElement(component) {
            var svgDOMelement = angular.element(
                $document[0].getElementById(component.CODE)
            )[0];
            if (svgDOMelement) {
                _setSvgElement(svgDOMelement, component);
            }
        }

        function _setSvgElement(svgElement, component) {
            _storeOriginalStyles(svgElement, component.CODE);

            _applyDefaultStylesAndAttributes(svgElement, component);

            _applyOutlineForIssues(svgElement, {
                id: component.CODE,
                color: component.STROKE_COLOR,
            });

            _applyHighlightIfSelectedForProgressUpdate(svgElement, component);

            _addDottedPatternIfItHasOutOfSequenceActivity(
                svgElement,
                component
            );

            _applyHatchPatternIfWaitingForConfirmationOrAvailable(
                svgElement,
                component
            );

            _applyCrossHatchPatternIfRejected(svgElement, component);
        }

        function _storeOriginalStyles(svgElement, id) {
            originalStrokeStyles.set(
                id,
                svgElement.style.stroke ||
                    svgElement.getAttribute("stroke") ||
                    ""
            );
            originalStrokes.set(id, _getSourceStrokeWidth(svgElement));
        }

        function _applyDefaultStylesAndAttributes(svgElement, component) {
            svgElement.style.fill = component[vm.colorProperty];
            svgElement.style.fillOpacity = 1;
            svgElement.style.opacity = 1;
        }

        function resetStrokeStyles(svgElement, { id }) {
            svgElement.style.strokeWidth = originalStrokes.get(id);
            svgElement.style.stroke = originalStrokeStyles.get(id);
        }

        function setStrokeStyles(svgElement, { color }) {
            svgElement.style.stroke = color;
            svgElement.style.strokeWidth = _getVisibleStrokeWidth(svgElement);
        }

        function _getVisibleStrokeWidth(svgElement) {
            const { unit, width } = _parseStrokeWidth(
                _getSourceStrokeWidth(svgElement)
            );
            const scaledWidth = width * HOVER_BORDER_SCALE;
            const visibleWidth = Math.min(
                HOVER_BORDER_MAX_PX,
                Math.max(HOVER_BORDER_MIN_PX, scaledWidth)
            );

            return `${visibleWidth}${unit}`;
        }

        function _getSourceStrokeWidth(svgElement) {
            return (
                svgElement.getAttribute(ORIGINAL_STROKE_WIDTH_ATTRIBUTE) ||
                svgElement.style.strokeWidth ||
                svgElement.getAttribute("stroke-width") ||
                DEFAULT_SVG_STROKE_WIDTH.toString()
            );
        }

        function _parseStrokeWidth(cssWidth) {
            const matchedWidth = `${cssWidth}`.match(CSS_UNIT_VALUE_REGEX);

            if (!matchedWidth) {
                return {
                    unit: "",
                    width: DEFAULT_SVG_STROKE_WIDTH,
                };
            }

            return {
                unit: matchedWidth[2],
                width:
                    Number.parseFloat(matchedWidth[1]) ||
                    DEFAULT_SVG_STROKE_WIDTH,
            };
        }

        function _applyOutlineForIssues(svgElement, { id, color }) {
            resetStrokeStyles(svgElement, { id });
            if ($sbSketchVisualizationSetting.model.showBorder && color) {
                setStrokeStyles(svgElement, { color });
            }
        }

        function _applyHoverOutline(svgElement, { id, color }) {
            resetStrokeStyles(svgElement, { id });
            setStrokeStyles(svgElement, { color });
        }

        function _getInteractiveTarget(target) {
            if (!target) {
                return undefined;
            }

            const targetElement = _.isFunction(target.closest)
                ? target
                : target.parentElement;

            if (!targetElement || !_.isFunction(targetElement.closest)) {
                return undefined;
            }

            return targetElement.closest(CLICKABLE_SELECTOR) || undefined;
        }

        function executeOnEvent($event, callback) {
            const target = _getInteractiveTarget($event.target);

            if (!target) {
                return;
            }

            const id = target.getAttribute("deliverable");
            const component = _.find(vm.components, ["ID", id]);
            if (component) {
                callback(target, component);
            }
        }

        function revertHoverColor($event) {
            executeOnEvent($event, (svgElement, component) =>
                _applyOutlineForIssues(svgElement, {
                    color: component.STROKE_COLOR,
                    id: component.CODE,
                })
            );
        }

        function placeHoverColor($event) {
            executeOnEvent($event, (svgElement, component) =>
                _applyHoverOutline(svgElement, {
                    color: "#365781",
                    id: component.CODE,
                })
            );
        }

        function _updateSvgBorderColors() {
            return $timeout(function () {
                vm.components.forEach((component) => {
                    const svgDOMelement = angular.element(
                        $document[0].getElementById(component.CODE)
                    )[0];
                    if (svgDOMelement) {
                        _applyOutlineForIssues(svgDOMelement, {
                            id: component.CODE,
                            color: component.STROKE_COLOR,
                        });
                    }
                });
                _updateStructureBorderColors();
            }, 0);
        }

        function _updateStructureBorderColors() {
            const svgContainer = $document[0].getElementById(CONTAINER_ID);
            if (!svgContainer) {
                return;
            }
            const structureElements = svgContainer.querySelectorAll(
                '[data-sb-type="structure"]'
            );
            _.forEach(structureElements, (svgElement) => {
                const structureId = svgElement.id;
                const style = currentStructureStyles.get(structureId);
                if (!style) {
                    return;
                }
                _applyOutlineForIssues(svgElement, {
                    id: structureId,
                    color: style.strokeColor,
                });
            });
        }

        function _reMatchComponents() {
            return $timeout(function () {
                $sbSvgGenerate.resetSvgPatterns();
                _attachSvg(currentSvgCode, CONTAINER_ID);
                _updateCurrentPatternSize();
                _matchComponents(currentComponents, currentSvgCode);
                _colorStructureElements();
            }, 0);
        }

        function _colorStructureElements() {
            currentStructureStyles = new Map();
            if (!_isStageMode() && !_isBrowseActivityMode()) {
                return;
            }
            const svgContainer = $document[0].getElementById(CONTAINER_ID);
            if (!svgContainer) {
                return;
            }
            const structureElements = svgContainer.querySelectorAll(
                '[data-sb-type="structure"]'
            );
            if (!structureElements.length) {
                return;
            }
            const presentStructureIds = new Set(
                Array.from(structureElements, (el) => el.id).filter(Boolean)
            );
            const relevantComponents = currentComponents.filter(
                (component) =>
                    component.STRUCTURE_ID &&
                    presentStructureIds.has(component.STRUCTURE_ID)
            );
            currentStructureStyles =
                visualizationService.resolveStructureColors(
                    relevantComponents,
                    _isStageMode() ? MODES.STAGE : MODES.BROWSE_ACTIVITY,
                    $sbSketchVisualizationSetting.model.markOutOfSequence
                );
            _.forEach(
                structureElements,
                function applyStructureStyle(svgElement) {
                    const structureId = svgElement.id;
                    const style = currentStructureStyles.get(structureId);
                    if (!style) {
                        return;
                    }

                    svgElement.style.fill = style.color;
                    svgElement.style.fillOpacity = 1;
                    svgElement.style.opacity = 1;

                    _applyOutlineForIssues(svgElement, {
                        id: structureId,
                        color: style.strokeColor,
                    });

                    if (style.hatch) {
                        _applyColoredPatternToSvg(
                            svgElement,
                            "hatched",
                            style.color
                        );
                    }
                    if (style.crossHatch) {
                        _applyColoredPatternToSvg(
                            svgElement,
                            "crossHatched",
                            style.color
                        );
                    }
                    if (style.outOfSequence) {
                        _applyColoredPatternToSvg(
                            svgElement,
                            "dotted",
                            style.color
                        );
                    }
                }
            );
        }

        function _reportVisibleColors() {
            if (!_.isFunction(vm.onVisibleColorsChanged)) {
                return;
            }

            const svgContainer = $document[0].getElementById(CONTAINER_ID);
            if (!svgContainer) {
                return;
            }

            const visibleColors = [];
            currentComponents.forEach((component) => {
                const svgElement = $document[0].getElementById(component.CODE);
                if (svgElement && svgContainer.contains(svgElement)) {
                    visibleColors.push(
                        component[vm.colorProperty],
                        component.STROKE_COLOR
                    );
                }
            });

            if (_isStageMode() || _isBrowseActivityMode()) {
                currentStructureStyles.forEach((style) => {
                    visibleColors.push(style.color, style.strokeColor);
                });
            }

            vm.onVisibleColorsChanged({
                colors: _.uniq(_.compact(visibleColors)),
            });
        }

        function _applyHighlightIfSelectedForProgressUpdate(
            svgElement,
            component
        ) {
            if (_hasBeenSelectedForProgressUpdate(component)) {
                svgElement.classList.add("sb-svg-element--selected");
            } else {
                svgElement.classList.remove("sb-svg-element--selected");
            }
        }

        function _applyHatchPatternIfWaitingForConfirmationOrAvailable(
            svgElement,
            component
        ) {
            var isWaitingForConfirmationOrAvailable = !!component.HATCH_COLOR;

            if (
                isWaitingForConfirmationOrAvailable &&
                _isBrowseOrShowStatusMode()
            ) {
                _applyColoredPatternToSvg(
                    svgElement,
                    "hatched",
                    component[vm.colorProperty]
                );
            }
        }

        function _applyCrossHatchPatternIfRejected(svgElement, component) {
            var isRejected = !!component.CROSS_HATCH_COLOR;

            if (isRejected && _isBrowseOrShowStatusMode()) {
                _applyColoredPatternToSvg(
                    svgElement,
                    "crossHatched",
                    component[vm.colorProperty]
                );
            }
        }

        function _addDottedPatternIfItHasOutOfSequenceActivity(
            svgElement,
            component
        ) {
            if (!$sbSketchVisualizationSetting.model.markOutOfSequence) {
                return;
            }
            if (
                _isBrowseOrShowStatusMode() &&
                _shouldDisplayOutOfSequencePattern(component)
            ) {
                _applyColoredPatternToSvg(
                    svgElement,
                    "dotted",
                    component[vm.colorProperty]
                );
            }
        }

        function _shouldDisplayOutOfSequencePattern(component) {
            if (_isStageMode()) {
                return (
                    _isOutOfSequence(component) && _isDoneOrConfirmed(component)
                );
            }

            if (_isBrowseActivityMode()) {
                return !!component.HAS_OUT_OF_SEQUENCE_ACTIVITY_ON_SELECTED_ACTIVITY;
            }

            return false;
        }

        function _isOutOfSequence(component) {
            return !!component.HAS_OUT_OF_SEQUENCE_ACTIVITY;
        }

        function _isDoneOrConfirmed(component) {
            return _.includes(
                STATES_THAT_CAN_BE_COMBINED_WITH_OUT_OF_SEQUENCE,
                _.get(component, "STAGE.STATE")
            );
        }

        function _isStageMode() {
            return vm.colorProperty === MODES.STAGE.colorProperty;
        }

        function _isBrowseActivityMode() {
            return vm.colorProperty === MODES.BROWSE_ACTIVITY.colorProperty;
        }

        function _applyColoredPatternToSvg(svgElement, patternType, color) {
            var svg = $sbSvgGenerate.getSvg(CONTAINER_ID);
            var pattern = _getOrCreatePatternOn(
                svg,
                patternType,
                color,
                currentPatternSize
            );
            svgElement.style.fill = pattern.fill();
        }

        function _isBrowseOrShowStatusMode() {
            return (
                vm.colorProperty === MODES.STAGE.colorProperty ||
                vm.colorProperty === MODES.BROWSE_ACTIVITY.colorProperty
            );
        }

        function _hasBeenSelectedForProgressUpdate(component) {
            return (
                _.isArray(currentSelection) &&
                currentSelection.indexOf(component.ID) !== -1
            );
        }

        function _clickElement(event) {
            const target = _getInteractiveTarget(event.target);

            if (_.isFunction(vm.onComponentClick)) {
                vm.onComponentClick(target || event.target);
            }
        }

        function _getOrCreatePatternOn(svg, type, color, patternSize) {
            let pattern = $sbSvgGenerate.findPatternBy(type, color);

            // when not existing, create and find it
            //
            if (_.isUndefined(pattern)) {
                $sbSvgGenerate.createPatternOnSvg(svg, color, patternSize);
                pattern = $sbSvgGenerate.findPatternBy(type, color);
            }

            return pattern;
        }

        function _updateCurrentPatternSize() {
            const svgContainer = $document[0].getElementById(CONTAINER_ID);
            if (!svgContainer) {
                currentPatternSize = $sbSvgGenerate.computePatternSize([]);
                return;
            }
            const clickableElements = Array.from(
                svgContainer.querySelectorAll(CLICKABLE_SELECTOR)
            );
            currentPatternSize =
                $sbSvgGenerate.computePatternSize(clickableElements);
        }
    },
};
